import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Wind,
  Anchor,
  Eye,
  MessageSquare,
  CheckCircle2,
  RotateCcw,
} from "lucide-react";

export interface OutlineStage {
  stage: number;
  name: string;
  duration_estimate: string;
  starting_state: string;
  ending_state: string;
  breath_technique: string;
  anchor: string;
  key_sensory_moments: string[];
  key_phrases: string[];
}

export interface Outline {
  session_type: string;
  total_duration_estimate: string;
  emotional_arc: string;
  stages: OutlineStage[];
  pettlep_notes: string;
  narrative_coherence_notes: string;
}

interface VizOutlineReviewProps {
  outline: Outline;
  onApprove: () => void;
  onChangeRequest: (request: string) => void;
  onBack: () => void;
}

const VizOutlineReview = ({
  outline,
  onApprove,
  onChangeRequest,
  onBack,
}: VizOutlineReviewProps) => {
  const [showPettlep, setShowPettlep] = useState(false);
  const [showChangeRequest, setShowChangeRequest] = useState(false);
  const [changeRequestText, setChangeRequestText] = useState("");

  const handleSubmitChangeRequest = () => {
    if (!changeRequestText.trim()) return;
    onChangeRequest(changeRequestText.trim());
  };

  return (
    <div className="min-h-screen gradient-hero flex flex-col">
      <header className="p-4 md:p-6 flex items-center gap-4 border-b border-border/30">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-display text-lg font-semibold">Your VIZ Outline</h1>
          <p className="text-xs text-muted-foreground">Review before generating your session</p>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6">
        <div className="max-w-2xl mx-auto space-y-5">

          {/* Session header card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-border/40 bg-secondary/30 p-5 space-y-3"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {outline.session_type} session
              </span>
              <span className="text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full font-medium">
                ~{outline.total_duration_estimate}
              </span>
            </div>
            <p className="text-sm font-medium leading-relaxed">{outline.emotional_arc}</p>
          </motion.div>

          {/* Stage cards */}
          {outline.stages.map((stage, i) => (
            <motion.div
              key={stage.stage}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="rounded-2xl border border-border/40 bg-card/60 p-5 space-y-4"
            >
              {/* Stage header */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full gradient-cta flex items-center justify-center text-xs font-bold text-primary-foreground flex-shrink-0">
                    {stage.stage}
                  </span>
                  <span className="font-semibold text-sm">{stage.name}</span>
                </div>
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {stage.duration_estimate}
                </span>
              </div>

              {/* Emotional transition */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                <span className="px-2.5 py-1 rounded-full bg-secondary/60 border border-border/30">
                  {stage.starting_state}
                </span>
                <span className="text-muted-foreground/50">→</span>
                <span className="px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary">
                  {stage.ending_state}
                </span>
              </div>

              {/* Breath + anchor callouts */}
              <div className="space-y-2">
                {stage.breath_technique && (
                  <div className="flex items-start gap-2 text-xs">
                    <Wind className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{stage.breath_technique}</span>
                  </div>
                )}
                {stage.anchor && (
                  <div className="flex items-start gap-2 text-xs">
                    <Anchor className="w-3.5 h-3.5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{stage.anchor}</span>
                  </div>
                )}
              </div>

              {/* Sensory moments */}
              {stage.key_sensory_moments.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70 font-medium">
                    <Eye className="w-3 h-3" />
                    Sensory moments
                  </div>
                  <ul className="space-y-1">
                    {stage.key_sensory_moments.map((m, j) => (
                      <li
                        key={j}
                        className="text-xs text-muted-foreground pl-4 relative before:absolute before:left-1.5 before:top-1.5 before:w-1 before:h-1 before:rounded-full before:bg-muted-foreground/40"
                      >
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key phrases */}
              {stage.key_phrases.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70 font-medium">
                    <MessageSquare className="w-3 h-3" />
                    Key phrases
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {stage.key_phrases.map((p, j) => (
                      <span
                        key={j}
                        className="text-xs px-2.5 py-1 rounded-full bg-secondary/60 border border-border/30 text-foreground/80 italic"
                      >
                        "{p}"
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ))}

          {/* AI Coaching Notes (collapsible) */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: outline.stages.length * 0.06 }}
            className="rounded-2xl border border-border/40 bg-secondary/20 overflow-hidden"
          >
            <button
              className="w-full flex items-center justify-between px-5 py-4 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setShowPettlep(!showPettlep)}
            >
              <span>AI Coaching Notes</span>
              {showPettlep ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <AnimatePresence>
              {showPettlep && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-5 space-y-4 text-xs text-muted-foreground leading-relaxed">
                    <div>
                      <p className="font-semibold text-foreground/70 mb-1">PETTLEP Application</p>
                      <p>{outline.pettlep_notes}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground/70 mb-1">Narrative Coherence</p>
                      <p>{outline.narrative_coherence_notes}</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Change request input */}
          <AnimatePresence>
            {showChangeRequest && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="rounded-2xl border border-border/40 bg-card/60 p-5 space-y-3">
                  <p className="text-sm font-medium">What would you like changed?</p>
                  <p className="text-xs text-muted-foreground">
                    E.g. "Use a word as my anchor instead of a fist clench" or "The venue is indoors, not outdoors"
                  </p>
                  <Textarea
                    value={changeRequestText}
                    onChange={(e) => setChangeRequestText(e.target.value)}
                    placeholder="Describe what you'd like adjusted..."
                    className="resize-none text-sm"
                    rows={3}
                    autoFocus
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowChangeRequest(false);
                        setChangeRequestText("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="hero"
                      size="sm"
                      onClick={handleSubmitChangeRequest}
                      disabled={!changeRequestText.trim()}
                    >
                      Regenerate Outline
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="border-t border-border/30 p-4 md:p-6">
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-3">
          {!showChangeRequest && (
            <Button
              variant="outline"
              className="flex-1 gap-2"
              onClick={() => setShowChangeRequest(true)}
            >
              <RotateCcw className="w-4 h-4" />
              Request Changes
            </Button>
          )}
          <Button
            variant="hero"
            className="flex-1 gap-2"
            onClick={onApprove}
          >
            <CheckCircle2 className="w-4 h-4" />
            Generate My VIZ
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VizOutlineReview;
